/**
 * Security surfaces contract (Normal Core MASTER-PRD §6.5.6, #918) — the
 * Team → Roles & permissions editor and the Workspace → Data encryption +
 * audit-chain cards. Read rollups + the grant/assign/verify mutations.
 * Owner-gated in the routes; deliberately NOT MCP tools (agents go through
 * the core helpers; these are human-UI affordances).
 */

import { z } from "zod";
import type { Operation } from "../types";

export const securityRoleSchema = z.object({
  uid: z.string(),
  slug: z.string(),
  name: z.string(),
  builtin: z.boolean(),
  grants: z.array(z.string()),
  actorCount: z.number(),
});
export type SecurityRole = z.infer<typeof securityRoleSchema>;

export const securityActorSchema = z.object({
  externalId: z.string(),
  displayName: z.string().nullable(),
  kind: z.string(),
  roleSlug: z.string().nullable(),
});
export type SecurityActor = z.infer<typeof securityActorSchema>;

export const securityAclRowSchema = z.object({
  entityKind: z.string(),
  entityUid: z.string(),
  actorExternalId: z.string().nullable(),
  roleSlug: z.string().nullable(),
  capability: z.string(),
  createdAt: z.string(),
});
export type SecurityAclRow = z.infer<typeof securityAclRowSchema>;

const rolesPayload = z.object({
  engaged: z.boolean(),
  roles: z.array(securityRoleSchema),
  actors: z.array(securityActorSchema),
  acl: z.array(securityAclRowSchema),
});
export type SecurityRolesPayload = z.infer<typeof rolesPayload>;

const rolesOp: Operation<void, void, void, SecurityRolesPayload> = {
  method: "GET",
  path: () => "/api/security/roles",
  response: rolesPayload,
};

const statusPayload = z.object({
  encryption: z.object({
    encrypted: z.boolean(),
    cipherVersion: z.string().nullable(),
    keySource: z.enum(["env", "keychain", "file", "none"]),
    // KEY-MANAGEMENT-SPEC §7.2 (T12): live-computed degraded conditions.
    degradations: z.array(
      z.object({
        id: z.string(),
        severity: z.enum(["high", "medium"]),
        acknowledged: z.boolean(),
      }),
    ),
  }),
  chain: z.object({
    checkpointId: z.number().nullable(),
    headHash: z.string().nullable(),
    signed: z.boolean(),
    eventCount: z.number().nullable(),
    createdAt: z.string().nullable(),
    totalEvents: z.number(),
  }),
  graph: z.object({
    engaged: z.boolean(),
    roleCount: z.number(),
    grantCount: z.number(),
    aclCount: z.number(),
    assignedActors: z.number(),
  }),
});
export type SecurityStatusPayload = z.infer<typeof statusPayload>;

// statusOp before mutations so invalidates can name it by value (RO-5-02a).
const statusOp: Operation<void, void, void, SecurityStatusPayload> = {
  method: "GET",
  path: () => "/api/security/status",
  response: statusPayload,
};

const seedRolesOp: Operation<void, void, void, { ok: boolean }> = {
  method: "POST",
  path: () => "/api/security/seed-roles",
  response: z.object({ ok: z.boolean() }),
  // roles list + status.graph counters both move.
  invalidates: [rolesOp, statusOp],
};

const assignBody = z.object({
  actorExternalId: z.string().min(1),
  roleSlug: z.string().min(1).nullable(),
});
const assignRoleOp: Operation<
  void,
  void,
  z.infer<typeof assignBody>,
  { ok: boolean; reason?: string }
> = {
  method: "POST",
  path: () => "/api/security/assign-role",
  body: assignBody,
  response: z.object({ ok: z.boolean(), reason: z.string().optional() }),
  invalidates: [rolesOp, statusOp],
};

const grantBody = z.object({
  roleSlug: z.string().min(1),
  capability: z.string().min(1),
  remove: z.boolean().optional(),
});
const grantOp: Operation<
  void,
  void,
  z.infer<typeof grantBody>,
  { ok: boolean; reason?: string }
> = {
  method: "POST",
  path: () => "/api/security/grant",
  body: grantBody,
  response: z.object({ ok: z.boolean(), reason: z.string().optional() }),
  invalidates: [rolesOp, statusOp],
};

const verifyPayload = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  checkpoints: z.number().optional(),
  checkpointId: z.number().optional(),
  brokenAt: z.string().optional(),
});
export type SecurityVerifyPayload = z.infer<typeof verifyPayload>;

const verifyChainOp: Operation<void, void, void, SecurityVerifyPayload> = {
  method: "POST",
  path: () => "/api/security/verify-chain",
  response: verifyPayload,
  invalidates: [statusOp],
};

const restorePointsPayload = z.object({
  points: z.array(
    z.object({
      checkpointId: z.number().nullable(),
      at: z.string(),
      eventCount: z.number(),
      signed: z.boolean(),
    }),
  ),
  log: z.object({
    totalEvents: z.number(),
    firstAt: z.string().nullable(),
    lastAt: z.string().nullable(),
  }),
});
export type RestorePointsPayload = z.infer<typeof restorePointsPayload>;

const restorePointsOp: Operation<void, void, void, RestorePointsPayload> = {
  method: "GET",
  path: () => "/api/security/restore-points",
  response: restorePointsPayload,
};

const previewQuery = z.object({ t: z.string().min(1) });
const restorePreviewPayload = z.object({
  upToTime: z.string(),
  eventsAfter: z.number(),
  kinds: z.array(z.object({ kind: z.string(), atT: z.number(), now: z.number() })),
  latestCheckpointAt: z.string().nullable(),
});
export type RestorePreviewPayload = z.infer<typeof restorePreviewPayload>;

const restorePreviewOp: Operation<
  void,
  z.infer<typeof previewQuery>,
  void,
  RestorePreviewPayload
> = {
  method: "GET",
  path: () => "/api/security/restore-preview",
  query: previewQuery,
  response: restorePreviewPayload,
};

const prepareBody = z.object({ t: z.string().min(1) });
const prepareRestorePayload = z.object({
  ok: z.boolean(),
  stagingPath: z.string().optional(),
  applied: z.number().optional(),
  reason: z.string().optional(),
});
export type PrepareRestorePayload = z.infer<typeof prepareRestorePayload>;

const prepareRestoreOp: Operation<
  void,
  void,
  z.infer<typeof prepareBody>,
  PrepareRestorePayload
> = {
  method: "POST",
  path: () => "/api/security/prepare-restore",
  body: prepareBody,
  response: prepareRestorePayload,
  invalidates: [restorePointsOp],
};

export const security = {
  roles: rolesOp,
  restorePoints: restorePointsOp,
  restorePreview: restorePreviewOp,
  prepareRestore: prepareRestoreOp,
  seedRoles: seedRolesOp,
  assignRole: assignRoleOp,
  grant: grantOp,
  status: statusOp,
  verifyChain: verifyChainOp,
};
